import { api } from "./apiClient";
import { Empresa } from "@/types/empresa";

/** Normaliza empresa desde API: convierte snake_case a camelCase y elimina duplicados snake_case */
function normalizeEmpresa(raw: Record<string, unknown>): Empresa {
  const e = raw as unknown as Empresa & {
    vobo_requerido?: string;
    asesor?: string;
    empresa_pagadora?: string;
    empresa_segmentacion_cobranza?: string;
    autorizacion_dg?: string;
    owner_id?: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = {
    ...e,
    voboRequerido: e.voboRequerido ?? e.vobo_requerido ?? undefined,
    asesorNombre: e.asesorNombre ?? e.asesor ?? undefined,
    empresaPagadora: e.empresaPagadora ?? e.empresa_pagadora ?? undefined,
    empresaSegmentacionCobranza: e.empresaSegmentacionCobranza ?? e.empresa_segmentacion_cobranza ?? undefined,
    autorizacionDG: e.autorizacionDG ?? e.autorizacion_dg ?? undefined,
    ownerID: e.ownerID ?? e.owner_id ?? undefined,
  };
  // Eliminar keys snake_case para evitar que al enviar de vuelta al API
  // el alias (snake_case) sobreescriba el valor editado (camelCase)
  delete result.vobo_requerido;
  delete result.asesor;
  delete result.empresa_pagadora;
  delete result.empresa_segmentacion_cobranza;
  delete result.autorizacion_dg;
  delete result.owner_id;
  return result as Empresa;
}

export const empresaService = {
  getAll: async () => {
    const res = await api.get<Empresa[]>("/empresas/");
    if (res.data && Array.isArray(res.data)) {
      return { ...res, data: res.data.map((e) => normalizeEmpresa(e as unknown as Record<string, unknown>)) };
    }
    return res;
  },

  getRestringidas: () => api.get<string[]>("/empresas/restringidas"),

  getById: async (id: string) => {
    const res = await api.get<Empresa>(`/empresas/${id}`);
    if (res.data) {
      return { ...res, data: normalizeEmpresa(res.data as unknown as Record<string, unknown>) };
    }
    return res;
  },

  create: async (empresa: Partial<Empresa>) => {
    const res = await api.post<Empresa>("/empresas/", empresa);
    if (res.data) {
      return { ...res, data: normalizeEmpresa(res.data as unknown as Record<string, unknown>) };
    }
    return res;
  },

  update: async (id: string, empresa: Partial<Empresa>) => {
    const res = await api.put<Empresa>(`/empresas/${id}`, empresa);
    if (res.data) {
      return { ...res, data: normalizeEmpresa(res.data as unknown as Record<string, unknown>) };
    }
    return res;
  },

  getAsignaciones: (id: string) => api.get<string[]>(`/empresas/${id}/asignaciones`),
  setAsignacion: (id: string, owner_id: string, asignado: boolean) =>
    api.put<string[]>(`/empresas/${id}/asignaciones`, { ownerID: owner_id, asignado }),

  getAsignacionesView: (ownerId: string) =>
    api.get<AsignacionAsesorResponse[]>(`/empresas/asignaciones?owner_id=${ownerId}`),

  getEmpresasPorAsesor: async (ownerId: string) => {
    const res = await api.get<Empresa[]>(`/empresas/por-asesor/${encodeURIComponent(ownerId)}`, { cache: "no-store" });
    if (res.data && Array.isArray(res.data)) {
      return { ...res, data: res.data.map((e) => normalizeEmpresa(e as unknown as Record<string, unknown>)) };
    }
    return res;
  },
};

export interface AsignacionEmpresaItem {
  id: string;
  nombre: string;
  asignada: boolean;
}

export interface AsignacionAsesorResponse {
  asesor: string;
  ownerID: string;
  empresas: AsignacionEmpresaItem[];
}
